import React, { useState, useEffect } from 'react';
import { getPendingPayments, approvePayment, rejectPayment } from '../../services/payment';
import { getUserProfile } from '../../services/auth';

const PaymentApproval = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const pendingPayments = await getPendingPayments();
      
      // Fetch user details for each payment
      const paymentsWithUsers = await Promise.all(
        pendingPayments.map(async (payment) => {
          try {
            const userProfile = await getUserProfile(payment.userId);
            return {
              ...payment,
              userProfile
            };
          } catch (err) {
            return {
              ...payment,
              userProfile: { displayName: 'Unknown User', email: 'unknown@example.com' }
            };
          }
        })
      );
      
      setPayments(paymentsWithUsers);
    } catch (err) {
      setError('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId) => {
    if (processing.has(paymentId)) return;
    
    setProcessing(prev => new Set(prev).add(paymentId));
    setError('');
    setSuccess('');

    try {
      await approvePayment(paymentId, 'current_admin_uid'); // Replace with actual admin ID
      
      // Remove from pending list
      setPayments(prev => prev.filter(p => p.id !== paymentId));
      setSuccess('Payment approved successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to approve payment');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(paymentId);
        return newSet;
      });
    }
  };

  const handleReject = async () => {
    if (!selectedPayment || !rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    if (processing.has(selectedPayment.id)) return;
    
    setProcessing(prev => new Set(prev).add(selectedPayment.id));
    setError('');

    try {
      await rejectPayment(selectedPayment.id, 'current_admin_uid', rejectionReason);
      
      // Remove from pending list
      setPayments(prev => prev.filter(p => p.id !== selectedPayment.id));
      setSuccess('Payment rejected successfully!');
      
      // Close modal
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectionReason('');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to reject payment');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedPayment.id);
        return newSet;
      });
    }
  };

  const openRejectModal = (payment) => {
    setSelectedPayment(payment);
    setShowRejectModal(true);
    setRejectionReason('');
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedPayment(null);
    setRejectionReason('');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getLevelName = (levelId) => {
    const levels = {
      grade7: 'Grade 7',
      form2: 'Form 2',
      grade12: 'Grade 12'
    };
    return levels[levelId] || levelId;
  };

  if (loading) {
    return (
      <div className="payment-approval">
        <div className="loading-container">
          <div className="loading-spinner">Loading pending payments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-approval">
      <div className="container">
        <div className="page-header">
          <h1>Payment Approval</h1>
          <p>Review and approve pending subscription payments</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="approval-stats">
          <div className="stat-card">
            <h3>{payments.length}</h3>
            <p>Pending Payments</p>
          </div>
        </div>

        {payments.length === 0 ? (
          <div className="empty-state">
            <h3>No Pending Payments</h3>
            <p>All payments have been processed. Check back later for new submissions.</p>
          </div>
        ) : (
          <div className="payments-list">
            {payments.map((payment) => (
              <div key={payment.id} className="payment-card">
                <div className="payment-header">
                  <div className="user-info">
                    <h4>{payment.userProfile.displayName}</h4>
                    <p>{payment.userProfile.email}</p>
                  </div>
                  <div className="payment-amount">
                    <span className="amount">K{payment.amount}</span>
                    <span className="level">{getLevelName(payment.levelId)}</span>
                  </div>
                </div>

                <div className="payment-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Phone Number:</label>
                      <span>{payment.phoneNumber}</span>
                    </div>
                    <div className="detail-item">
                      <label>Transaction ID:</label>
                      <span>{payment.transactionId}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Date:</label>
                      <span>{formatDate(payment.paymentDate)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Submitted:</label>
                      <span>{formatDate(payment.createdAt)}</span>
                    </div>
                  </div>

                  {payment.notes && (
                    <div className="payment-notes">
                      <label>Notes:</label>
                      <p>{payment.notes}</p>
                    </div>
                  )}

                  {payment.proofOfPaymentUrl && (
                    <div className="payment-proof">
                      <label>Proof of Payment:</label>
                      <a 
                        href={payment.proofOfPaymentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="proof-link"
                      >
                        View Proof
                      </a>
                    </div>
                  )}
                </div>

                <div className="payment-actions">
                  <button
                    onClick={() => handleApprove(payment.id)}
                    className="btn btn-primary"
                    disabled={processing.has(payment.id)}
                  >
                    {processing.has(payment.id) ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => openRejectModal(payment)}
                    className="btn btn-secondary"
                    disabled={processing.has(payment.id)}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectModal && selectedPayment && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Reject Payment</h3>
                <button onClick={closeRejectModal} className="modal-close">×</button>
              </div>
              
              <div className="modal-content">
                <div className="payment-summary">
                  <p><strong>User:</strong> {selectedPayment.userProfile.displayName}</p>
                  <p><strong>Amount:</strong> K{selectedPayment.amount}</p>
                  <p><strong>Transaction ID:</strong> {selectedPayment.transactionId}</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="rejectionReason">Reason for Rejection *</label>
                  <textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="4"
                    placeholder="Please explain why this payment is being rejected..."
                    required
                  ></textarea>
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  onClick={handleReject}
                  className="btn btn-danger"
                  disabled={processing.has(selectedPayment.id)}
                >
                  {processing.has(selectedPayment.id) ? 'Processing...' : 'Reject Payment'}
                </button>
                <button onClick={closeRejectModal} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentApproval;
